import * as React from "react"
import { AnimatePresence, motion } from "framer-motion"
import { StaticImage } from "gatsby-plugin-image"
import * as styles from "./howItWorks.module.scss"

// StaticImage требует литеральный src, а шаги приходят массивом — маппим по id.
const StepImage: React.FC<{ id: number; alt: string; className?: string }> = ({ id, alt, className }) => {
  switch (id) {
    case 0:
      return <StaticImage src="./assets/step1.png" alt={alt} className={className} loading="lazy" placeholder="blurred" />
    case 1:
      return <StaticImage src="./assets/step2.png" alt={alt} className={className} loading="lazy" placeholder="blurred" />
    case 2:
      return <StaticImage src="./assets/step3.png" alt={alt} className={className} loading="lazy" placeholder="blurred" />
    default:
      return null
  }
}

import Tabs from "@shared/uiNew/chat/tabs"
import { steps } from "@shared/uiNew/chat/data"
import useIsMobile from "@shared/uiNew/chat/useIsMobile"
import useMobileScrollSync from "@shared/uiNew/chat/useMobileScrollSync"
import useDesktopScrollSync from "@shared/uiNew/chat/useDesktopScrollSync"
import useTabsAutoScroll from "@shared/uiNew/chat/useTabsAutoScroll"
import useHowItWorksNavigation from "@shared/uiNew/chat/useHowItWorksNavigation"
import SectionTitle from "@shared/uiNew/chat/sectionTitle"
import Button from "@shared/uiNew/Button/button"

export const HowItWorksSection: React.FC = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const [current, setCurrent] = React.useState(0)
  const [isManual, setIsManual] = React.useState(false)

  const stepCount = steps.length
  const threshold = 800

  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([])
  const stepRefs = React.useRef<(HTMLDivElement | null)[]>([])

  const isMobile = useIsMobile(820)

  useMobileScrollSync(isMobile, stepRefs, current, setCurrent)
  useDesktopScrollSync(isMobile, scrollRef, current, setCurrent, stepCount, isManual)
  useTabsAutoScroll(current, tabRefs)

  const handleTabClick = useHowItWorksNavigation({
    isMobile,
    scrollRef,
    stepRefs,
    setCurrent,
    setIsManual,
    threshold,
  })

  return (
    <section className={styles.section}>
      <SectionTitle threshold={0.2} />
      <p className={styles.desc}>
        Не нужно внедрять разные сервисы для каждой команды. Онлайн-чат Carrot quest объединяет задачи трех отделов в одном интерфейсе, экономя время
        сотрудников и бюджет компании
      </p>
      <div className={styles.container}>
        {isMobile ? (
          <MobileLayout steps={steps} current={current} onTabClick={handleTabClick} tabRefs={tabRefs} stepRefs={stepRefs} />
        ) : (
          <DesktopLayout steps={steps} current={current} onTabClick={handleTabClick} tabRefs={tabRefs} scrollRef={scrollRef} />
        )}
      </div>
    </section>
  )
}

export default HowItWorksSection

interface MobileLayoutProps {
  steps: typeof steps
  current: number
  onTabClick: (index: number) => void
  tabRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>
  stepRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
}

const MobileLayout: React.FC<MobileLayoutProps> = ({ steps, current, onTabClick, tabRefs, stepRefs }) => {
  return (
    <>
      <Tabs steps={steps} current={current} onTabClick={onTabClick} tabRefs={tabRefs} />

      <div className={styles.mobileSteps}>
        {steps.map((step, i) => (
          <div
            key={step.id}
            className={styles.mobileStep}
            ref={(el) => {
              stepRefs.current[i] = el
            }}
          >
            <StepImage id={step.id} alt={step.label} className={styles.image} />
            {step.text}
          </div>
        ))}
      </div>
    </>
  )
}

interface DesktopLayoutProps {
  steps: typeof steps
  current: number
  onTabClick: (index: number) => void
  tabRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
}

const DesktopLayout: React.FC<DesktopLayoutProps> = ({ steps, current, onTabClick, tabRefs, scrollRef }) => {
  return (
    <div className={styles.scrollArea} ref={scrollRef}>
      <div className={styles.stickyWrapper}>
        <div className={styles.header}>
          <Tabs steps={steps} current={current} onTabClick={onTabClick} tabRefs={tabRefs} />
        </div>
        <div className={styles.stickyInner}>
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              className={styles.slide}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -60 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              <div className={styles.textPart}>{steps[current].text}</div>

              <div className={styles.imagePart}>
                <StepImage id={steps[current].id} alt={steps[current].label} className={styles.image} />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <Button text="Оставить заявку на демо" icon="arrow" style={styles.button} />
      </div>
    </div>
  )
}
